/**
 * OctileTypes.ts
 * Core type definitions for the octile grid dungeon system.
 * Based on ADR-001 (Octile Grid), ADR-022 (Cell Types), ADR-023 (Proportions).
 *
 * The dungeon is a truncated square tiling: octagons (rooms) + squares (corridors).
 * Stored as a 19×19 interleaved array for a 10×10 octagon grid.
 */

// ─── Wall States ────────────────────────────────────────────

export type WallState =
    | 'SOLID'    // Full wall, no passage
    | 'OPEN'     // No wall, free passage
    | 'DOOR'     // Door frame + door, passable after interaction
    | 'GATE'     // Locked gate, passable after unlock
    | 'GRATE'    // Iron grating, projectiles/sound/light pass
    | 'WINDOW'   // Barred opening, projectiles/sound/light pass
    | 'SECRET'   // Appears solid, Discovery reveals, then acts as DOOR
    | 'NONE'     // Invisible — no wall rendered at all (merged room interiors)

export const PASSABLE_WALLS: readonly WallState[] = ['OPEN', 'DOOR', 'NONE'] as const
export const TRANSPARENT_WALLS: readonly WallState[] = ['OPEN', 'DOOR', 'GRATE', 'WINDOW', 'NONE'] as const

export const WALL_STATE_CYCLE: readonly WallState[] = [
    'SOLID', 'OPEN', 'DOOR', 'GATE', 'GRATE', 'WINDOW', 'SECRET', 'NONE'
] as const

// ─── Cell Types (44 across 7 categories) ────────────────────

export type CellType =
    // Structural (10)
    | 'empty' | 'passage' | 'junction' | 'hub'
    | 'stairs_up' | 'stairs_down' | 'shaft' | 'bridge' | 'gate' | 'secret'
    // Natural (10)
    | 'cave' | 'stalactite_cave' | 'stalagmite_cave' | 'crystal_cave'
    | 'mushroom_grotto' | 'water_pool' | 'underground_stream'
    | 'lava_cell' | 'ice_cave' | 'collapsed'
    // Constructed (6)
    | 'chamber' | 'pillared_hall' | 'alcove' | 'prison' | 'library' | 'forge'
    // Sacred (3)
    | 'shrine' | 'entrance' | 'boss_throne'
    // Treasure (3)
    | 'vault' | 'cache' | 'hoard'
    // Hazard (6)
    | 'trap_floor' | 'trap_walls' | 'trap_ceiling' | 'gauntlet' | 'puzzle' | 'mimic_den'
    // Combat (6)
    | 'guard_post' | 'patrol_route' | 'ambush' | 'nest' | 'arena' | 'barricade'

export const SAFE_ZONE_TYPES: readonly CellType[] = ['entrance', 'shrine'] as const

// ─── Themes (8 visual treatments) ───────────────────────────

export type CellTheme =
    | 'natural_cave' | 'ancient_stone' | 'crystal' | 'organic'
    | 'frozen' | 'volcanic' | 'overgrown' | 'shadow'

// ─── Floor Configurations (6 elevation variants) ────────────

export type FloorConfig =
    | 'flat' | 'raised_center' | 'sunken_center'
    | 'split_level' | 'sloped' | 'broken'

// ─── Connector Moods ────────────────────────────────────────

export type ConnectorMood =
    | 'tunnel' | 'passage' | 'grand' | 'cramped'
    | 'bridge' | 'flooded' | 'frozen' | 'volcanic' | 'overgrown'

// ─── Grid Cell Types ────────────────────────────────────────

export type GridCellKind = 'octagon' | 'connector' | 'void'

export interface GridPos {
    row: number
    col: number
}

// ─── Octagonal Cell (Room) ──────────────────────────────────

export interface OctileCell {
    kind: 'octagon'
    row: number
    col: number

    // 8 wall segments (clockwise from N: N, NE, E, SE, S, SW, W, NW)
    walls: [WallState, WallState, WallState, WallState,
            WallState, WallState, WallState, WallState]

    // Classification
    type: CellType
    theme: CellTheme
    floor: FloorConfig

    // Content (see ContentTypes.ts for full definitions)
    spawns: string[]        // Spawn config IDs at anchor points
    traps: string[]         // Trap config IDs
    loot: string[]          // Loot config IDs
    decorations: string[]   // Decoration preset IDs

    // Vertical
    stairsUp: boolean
    stairsDown: boolean

    // Clustering
    partOfRoom: string | null

    // Metadata
    difficulty: number      // 1-10
    active: boolean         // false = empty/void, not rendered
}

// ─── Square Connector (Corridor) ────────────────────────────

export interface SquareConnector {
    kind: 'connector'
    row: number
    col: number

    // 4 wall segments (N, E, S, W)
    walls: [WallState, WallState, WallState, WallState]

    // Visual treatment
    mood: ConnectorMood

    // Limited content
    traps: string[]         // Max 1 trap
    decorations: string[]

    // Clustering
    isPartOfRoom: string | null

    active: boolean
}

// ─── Grid Cell Union ────────────────────────────────────────

export type GridCell = OctileCell | SquareConnector | null

// ─── Layer ──────────────────────────────────────────────────

export interface OctileLayer {
    depth: number
    name: string
    theme: CellTheme
    grid: GridCell[][]      // [row][col] — 19×19 for 10×10 octagons
    ambientLight: string
    fogDensity: number
    ambientAudio: string
}

// ─── Dungeon ────────────────────────────────────────────────

export interface OctileDungeon {
    id: string
    name: string
    seed: number

    // Grid config
    octagonCols: number     // 10
    octagonRows: number     // 10
    gridWidth: number       // 19 (interleaved)
    gridHeight: number      // 19

    // Proportions (ADR-023)
    baseSquareSize: number  // 12.8m
    cornerCutRatio: number  // 0.20
    corridorScale: 'spacious' | 'standard' | 'cramped'

    // Layers
    layers: OctileLayer[]

    // Entrance
    entrance: GridPos & { layer: number }

    // Identity
    dna: import('./GrowthTypes.ts').DungeonDNA

    // Metadata
    createdAt: string
    modifiedAt: string
    version: string
}

// ─── Grid Config Constants ──────────────────────────────────

export const GRID_DEFAULTS = {
    octagonCols: 10,
    octagonRows: 10,
    baseSquareSize: 12.8,
    cornerCutRatio: 0.20,
    corridorScale: 'standard' as const,
    floorHeight: 5,
} as const

export const CORRIDOR_SCALES = {
    spacious: 0.293,
    standard: 0.20,
    cramped: 0.15,
} as const

// ─── Factory Functions ──────────────────────────────────────

export function createDefaultOctileCell(row: number, col: number): OctileCell {
    return {
        kind: 'octagon',
        row, col,
        walls: ['SOLID', 'SOLID', 'SOLID', 'SOLID', 'SOLID', 'SOLID', 'SOLID', 'SOLID'],
        type: 'empty',
        theme: 'natural_cave',
        floor: 'flat',
        spawns: [],
        traps: [],
        loot: [],
        decorations: [],
        stairsUp: false,
        stairsDown: false,
        partOfRoom: null,
        difficulty: 1,
        active: false,
    }
}

export function createDefaultConnector(row: number, col: number): SquareConnector {
    return {
        kind: 'connector',
        row, col,
        walls: ['SOLID', 'SOLID', 'SOLID', 'SOLID'],
        mood: 'tunnel',
        traps: [],
        decorations: [],
        isPartOfRoom: null,
        active: false,
    }
}
